import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Home, Loader2, Flower2 } from "lucide-react";
import { ProNavigation } from "@/components/ProNavigation";

const ImportBachFlowers: React.FC = () => {
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-bach-flowers");

      if (error) throw error;

      toast({
        title: "Import réussi",
        description: data.message || "Les fleurs de Bach ont été importées avec succès",
      });

      // Rediriger vers la bibliothèque des fleurs de Bach
      setTimeout(() => navigate("/bach-flowers"), 1500);
    } catch (error: any) {
      console.error("Erreur import:", error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de l'import",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <ProNavigation />
      
      <div className="container mx-auto px-4 py-8 space-y-8">

        {/* En-tête */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Flower2 className="w-10 h-10 text-primary" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary">Import des Fleurs de Bach</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Importer les 38 fleurs de Bach originales dans votre base de données
          </p>
        </div>

        {/* Carte d'import */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Importer les Fleurs de Bach</CardTitle>
            <CardDescription>
              Cette action va importer les 38 fleurs de Bach originales du Dr Edward Bach avec leurs descriptions,
              effets, indications et protocoles d'utilisation complets.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <h3 className="font-semibold">Contenu de l'import :</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>38 fleurs de Bach originales</li>
                <li>Descriptions détaillées</li>
                <li>Effets principaux</li>
                <li>Mots-clés de symptômes</li>
                <li>Protocoles d'utilisation</li>
                <li>Contre-indications</li>
              </ul>
            </div>

            <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg">
              <p className="text-sm">
                <strong>Note :</strong> Si des fleurs de Bach existent déjà dans votre base de données, elles
                seront ignorées pour éviter les doublons.
              </p>
            </div>

            <Button
              onClick={handleImport}
              disabled={isImporting}
              className="w-full"
              size="lg"
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Flower2 className="w-4 h-4 mr-2" />
                  Lancer l'import
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Information */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>À propos des Fleurs de Bach</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Les Fleurs de Bach sont des élixirs floraux créés dans les années 1930 par le Dr Edward Bach,
              médecin homéopathe et bactériologiste britannique. Il a développé 38 quintessences florales
              destinées à harmoniser les états émotionnels.
            </p>
            <p>
              Chaque fleur correspond à un état émotionnel particulier et vise à restaurer l'équilibre
              émotionnel de la personne. Les Fleurs de Bach s'inscrivent dans une approche holistique et
              complémentaire du bien-être.
            </p>
            <p className="font-semibold">
              Important : Les Fleurs de Bach ne remplacent pas un traitement médical et ne dispensent pas
              de consulter un professionnel de santé.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportBachFlowers;